// src/utils/documentSize.ts
// Size units shared by every card that reports "how much of a document is loaded".
//
// Character counts are what the backends return, but they are not a unit a lawyer
// can act on — "48,210 characters" answers nothing about whether this is a whole
// contract or a fragment. Coverage does: what matters is whether the whole
// document is in context or only part of it.

/**
 * Estimate page count from character count (~3000 chars/page).
 *
 * A heuristic, not pagination: it knows nothing about the document's real page
 * breaks, so a card that prints "~13 pages" next to a PDF the reader can see is
 * 40 pages long has simply lied to them. Prefer `percentLoaded` for the
 * "how much is here?" question and keep this for cards that have no total to
 * measure against.
 */
export function estimatePages(charCount: number): number {
  return Math.max(1, Math.round(charCount / 3000));
}

/**
 * Portion of a document that made it into context, as a whole percent, or null
 * when the pair can't support a ratio (either count missing, or a total of zero
 * that would divide into nonsense).
 *
 * Rounding never reaches an endpoint from the inside: a partial load prints at
 * most 99% and, when anything at all came through, at least 1%. "100% loaded"
 * sitting above "only part of this document is loaded" reads as a bug, and a
 * rounded-down "0%" denies text that is demonstrably there. A genuinely empty
 * load still returns 0 — that one is not a rounding artefact.
 */
export function percentLoaded(
  returned: number | undefined,
  total: number | undefined
): number | null {
  if (returned == null || total == null) return null;
  if (!Number.isFinite(returned) || !Number.isFinite(total)) return null;
  if (total <= 0 || returned < 0) return null;
  if (returned >= total) return 100;
  if (returned === 0) return 0;

  return Math.min(99, Math.max(1, Math.round((returned / total) * 100)));
}
